// Haptic feedback helpers.
//
// Uses the Vibration API where available (most Android browsers + some
// desktop browsers). Silently no-ops on iOS Safari (which does not expose
// the Vibration API) and on any browser where vibration is disabled. The
// goal is taste, not reliance — never block user interaction on haptics.
//
// Respects a user preference stored in localStorage under `wipe_haptics`:
//   - 'off'       → always no-op
//   - anything    → default on

const STORAGE_KEY = 'wipe_haptics';

function canVibrate(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (!('vibrate' in navigator)) return false;
  try {
    if (localStorage.getItem(STORAGE_KEY) === 'off') return false;
  } catch { /* ignore */ }
  return true;
}

function buzz(pattern: number | number[]): void {
  if (!canVibrate()) return;
  try {
    navigator.vibrate(pattern);
  } catch { /* ignore */ }
}

/** Subtle tap — used for button presses that aren't state-changing. */
export function hapticTap(): void {
  buzz(10);
}

/** Standard success tick — vote cast, post submitted, bookmark toggled. */
export function hapticSuccess(): void {
  buzz([14, 40, 14]);
}

/** Warning buzz — not enough time in wallet, cap reached, etc. */
export function hapticWarning(): void {
  buzz([32, 40, 32]);
}

/** Error buzz — request failed, validation error. */
export function hapticError(): void {
  buzz([60, 50, 60, 50, 60]);
}

/** Heavy thump — permanence (big spend), streak milestone, referral bonus. */
export function hapticHeavy(): void {
  buzz([80, 30, 80]);
}

/**
 * Let callers flip haptics off entirely. Persists across sessions.
 * Settings UI can wire this to a toggle.
 */
export function setHapticsEnabled(enabled: boolean): void {
  try {
    if (enabled) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, 'off');
  } catch { /* ignore */ }
}

export function getHapticsEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}
